<template>
  <v-form ref="form" v-model="valid" validate-on="input">
    <v-alert
      v-if="isLegacy"
      :closable="false"
      type="warning"
      icon="icon-warning-xs"
      color="yellow-faint"
      class="warning-block text-yellow-base py-2 px-2"
      data-testid="siem-legacy-connection-alert"
    >
      <div class="subtitle1 text-yellow-base">
        {{ $t("modals.addSiemConnector.legacyBanner.title") }}
      </div>
      <div class="body2" v-html="$t('modals.addSiemConnector.legacyBanner.description')"></div>
    </v-alert>
    <v-text-field
      v-model="localValue.name"
      class="mt-6"
      :label="localValue.name ? $t('general.name') : ''"
      :aria-label="$t('general.name')"
      variant="outlined"
      :rules="[required()]"
      :disabled="isLegacy"
      :placeholder="$t('general.name')"
    />
    <v-select
      menu-icon="icon-arrow-down-xs"
      :class="{ 'filter-active': localValue.format }"
      :placeholder="$t('general.format')"
      v-model="localValue.format"
      class="mt-6"
      :label="$t('general.format')"
      :items="formatOptions"
      :disabled="!!localValue.id"
      variant="outlined"
    >
    </v-select>
    <template v-if="localValue.format === SiemConnectorFormat.AZURE_SENTINEL">
      <template v-if="isLegacy">
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().workspaceId"
          variant="outlined"
          disabled
          :label="$t(`modals.addSiemConnector.${localValue.format}.workspaceId`)"
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.workspaceId`)"
          data-testid="siem-legacy-workspace-id"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().primaryKey"
          type="password"
          variant="outlined"
          disabled
          :label="$t(`modals.addSiemConnector.${localValue.format}.primaryKey`)"
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.primaryKey`)"
          data-testid="siem-legacy-primary-key"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().logType"
          variant="outlined"
          disabled
          :label="$t(`modals.addSiemConnector.${localValue.format}.logType`)"
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.logType`)"
          data-testid="siem-legacy-log-type"
        />
      </template>
      <template v-else>
        <div class="info-block d-flex align-top px-3 py-2" data-testid="siem-sentinel-setup-help">
          <v-icon class="mr-2" size="24" icon="$help" />
          <span class="body2">
            {{ $t(`modals.addSiemConnector.${localValue.format}.setupDescription`) }}
            <a class="coro-link" :href="SENTINEL_DOCS_LINK" target="_blank" rel="noopener">
              {{ $t(`modals.addSiemConnector.${localValue.format}.needHelp`) }}
            </a>
          </span>
        </div>
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().tenantId"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().tenantId
              ? $t(`modals.addSiemConnector.${localValue.format}.tenantId`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.tenantId`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.tenantId`)"
          data-testid="siem-tenant-id"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().clientId"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().clientId
              ? $t(`modals.addSiemConnector.${localValue.format}.clientId`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.clientId`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.clientId`)"
          data-testid="siem-client-id"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().clientSecret"
          type="password"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().clientSecret
              ? $t(`modals.addSiemConnector.${localValue.format}.clientSecret`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.clientSecret`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.clientSecret`)"
          data-testid="siem-client-secret"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().dceEndpoint"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().dceEndpoint
              ? $t(`modals.addSiemConnector.${localValue.format}.dceEndpoint`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.dceEndpoint`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.dceEndpoint`)"
          data-testid="siem-dce-endpoint"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().dcrImmutableId"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().dcrImmutableId
              ? $t(`modals.addSiemConnector.${localValue.format}.dcrImmutableId`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.dcrImmutableId`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.dcrImmutableId`)"
          data-testid="siem-dcr-immutable-id"
        />
        <v-text-field
          class="mt-6"
          v-model="getCurrentSiemProvider().streamName"
          variant="outlined"
          :rules="[required()]"
          :label="
            getCurrentSiemProvider().streamName
              ? $t(`modals.addSiemConnector.${localValue.format}.streamName`)
              : ''
          "
          :aria-label="$t(`modals.addSiemConnector.${localValue.format}.streamName`)"
          :placeholder="$t(`modals.addSiemConnector.${localValue.format}.streamName`)"
          data-testid="siem-stream-name"
        />
      </template>
    </template>
    <template v-else>
      <v-text-field
        class="mt-6"
        v-model="getCurrentSiemProvider().url"
        :label="getCurrentSiemProvider().url ? $t('modals.addSiemConnector.listenerUrlLabel') : ''"
        :aria-label="
          getCurrentSiemProvider().url ? $t('modals.addSiemConnector.listenerUrlLabel') : ''
        "
        variant="outlined"
        :rules="urlRules"
        :placeholder="$t('modals.addSiemConnector.listenerUrlLabel')"
        :hint="$t('modals.addSiemConnector.listenerUrlHint')"
        persistent-hint
      />
      <key-value-input
        class="mt-6"
        v-if="localValue.format !== SiemConnectorFormat.FLUENCY"
        v-model:value="getCurrentSiemProvider().headers"
        :key-label="$t('modals.addSiemConnector.header')"
        :value-label="$t('modals.addSiemConnector.value')"
        :show-add-btn="localValue.format === SiemConnectorFormat.GENERIC"
        :key-config="{
          rules: headersRules,
          cols: 4,
          md: 4,
          disabled: localValue.format === SiemConnectorFormat.SPLUNK,
        }"
        :value-config="{
          rules: headersRules,
          cols: 8,
          md: 8,
        }"
        :add-btn-label="$t('modals.addSiemConnector.addBtn')"
      >
      </key-value-input>
      <v-text-field
        v-else
        v-model="getCurrentSiemProvider().headers![0].value"
        class="mt-6"
        :label="$t('modals.addSiemConnector.token')"
        :aria-label="$t('modals.addSiemConnector.token')"
        variant="outlined"
        :rules="headersRules"
        :placeholder="$t('modals.addSiemConnector.token')"
      />
    </template>
    <v-checkbox
      v-if="workspaceType !== WorkspaceType.CHILD"
      v-model="localValue.applyToAll"
      class="mt-6"
      :disabled="isLegacy"
      :ripple="false"
    >
      <template #label>
        <div class="body1">
          {{ $t("modals.addSiemConnector.applyToAll") }}
        </div>
      </template>
    </v-checkbox>
  </v-form>
</template>

<script lang="ts">
import Patterns from "@/constants/patterns";
import { computed, defineComponent, reactive, ref, watch } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";
import { SiemConnectorFormat } from "@/constants/connectors";
import { required } from "@/_helpers/validators";
import KeyValueInput from "@/components/KeyValueInput.vue";
import cloneDeep from "lodash/cloneDeep";
import type { SiemConnector, SiemConnectorProviderData } from "@/_store/connectors/siem.module";
import { isLegacySiemConnector } from "@/_store/connectors/siem.module";
import { WorkspaceType } from "@/constants/workplaces";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";

const SENTINEL_DOCS_LINK = "https://docs.coro.net/admin/siem-sentinel";

const defaultConnector = {
  name: "",
  format: SiemConnectorFormat.SPLUNK,
  applyToAll: false,
  [SiemConnectorFormat.SPLUNK]: {
    url: "",
    headers: [
      {
        key: "Authorization",
        value: "",
      },
    ],
  },
  [SiemConnectorFormat.FLUENCY]: {
    url: "",
    headers: [
      {
        key: "Authorization",
        value: "",
      },
    ],
  },
  [SiemConnectorFormat.AZURE_SENTINEL]: {
    tenantId: "",
    clientId: "",
    clientSecret: "",
    dceEndpoint: "",
    dcrImmutableId: "",
    streamName: "",
  },
  [SiemConnectorFormat.GENERIC]: {
    headers: [
      {
        key: "",
        value: "",
      },
    ],
  },
};

export default defineComponent({
  name: "AddSiemConnectorModal",
  components: { KeyValueInput },
  methods: { required },
  props: {
    config: {
      type: Object as PropType<{ item: SiemConnector; action: string }>,
      required: true,
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const valid = ref(false);
    const {
      workspace: { type: workspaceType },
    } = useWorkspaceStore();

    const localValue: Partial<SiemConnector> = reactive(
      cloneDeep(props.config.item || defaultConnector)
    );

    // Pre-migration connectors are read-only: their credentials cannot be migrated in place
    const isLegacy = computed(
      () => !!props.config.item && isLegacySiemConnector(props.config.item)
    );

    emit("update:localValue", localValue);

    const formatOptions = Object.values(SiemConnectorFormat)
      .filter((format) => format !== SiemConnectorFormat.SYSLOG)
      .map((value) => ({
        title: i18n.t(`connectors.siem.format.${value}`),
        value: value,
      }));

    watch(valid, (value: boolean) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true }
    );

    const form = ref();

    const validate = async () => {
      const isValid = await form.value.validate();

      return isValid.valid;
    };

    const urlRules = [
      (v: string) => {
        if (!Patterns.URL.test(v)) {
          return i18n.t("validations.url");
        }

        if (!v) {
          return i18n.t("validations.required");
        }

        return true;
      },
    ];
    const headersRules = [
      (v: string) => {
        if (!v && localValue.format !== SiemConnectorFormat.GENERIC) {
          return i18n.t("validations.required");
        }

        return true;
      },
    ];

    const onFormatChange = () => {
      form.value.resetValidation();
    };

    const getCurrentSiemProvider = (): SiemConnectorProviderData => {
      return localValue[localValue.format!]!;
    };

    return {
      valid,
      localValue,
      isLegacy,
      SENTINEL_DOCS_LINK,
      urlRules,
      headersRules,
      formatOptions,
      onFormatChange,
      SiemConnectorFormat,
      workspaceType,
      WorkspaceType,
      form,
      validate,
      getCurrentSiemProvider,
    };
  },
});
</script>

<style lang="scss" scoped>
.icon-help-xs::before {
  color: rgb(var(--v-theme-link));
}
</style>
